import { useState, useContext } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { Box, Typography, Link as MuiLink } from "@mui/material";
import { useTranslation } from "react-i18next";

import { auth } from "../config/FirebaseConfig";
import { UserContext } from "../context/UserContext";
import PageContainer from "../components/page-container/PageContainer";
import { FormTextField, FormPasswordField, FormSubmitButton } from "../components/form";
import AppSnackbar from "../components/snackbar/AppSnackbar";
import { useFormValidation, validators } from "../hooks/useFormValidation";
import { getErrorMessage } from "../utils/errorHandler";
import { FORM_CONTAINER_SX } from "../theme/formStyles";

const SignInPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const {
    handleBlur,
    clearFieldError,
    validateForm,
    getFieldError,
  } = useFormValidation();

  // Validation rules
  const validationRules = {
    email: [validators.required, validators.email],
    password: [validators.required],
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleEmailLogin = async (e) => {
    e.preventDefault();

    // Validate form before submission
    const formData = { email, password };
    if (!validateForm(formData, validationRules)) {
      return;
    }

    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setUser(userCredential.user);
      navigate("/project");
    } catch (err) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, t),
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Box
        component="form"
        onSubmit={handleEmailLogin}
        sx={FORM_CONTAINER_SX}
      >
        <Typography variant="h3" align="center" gutterBottom>
          {t("auth.sign_in")}
        </Typography>

        <FormTextField
          name="email"
          label={t("auth.email")}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearFieldError("email");
          }}
          onBlur={() => handleBlur("email", email, validationRules.email)}
          error={getFieldError("email", t)}
          required
        />

        <FormPasswordField
          name="password"
          label={t("auth.password")}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            clearFieldError("password");
          }}
          onBlur={() => handleBlur("password", password, validationRules.password)}
          error={getFieldError("password", t)}
          required
        />

        <FormSubmitButton
          label={t("auth.sign_in")}
          loading={loading}
        />

        <MuiLink
          component={RouterLink}
          to="/signup"
          variant="body2"
          display="block"
          align="center"
          sx={{ mt: 2 }}
        >
          {t("auth.no_account")}
        </MuiLink>
      </Box>

      <AppSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </PageContainer>
  );
};

export default SignInPage;
